import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import LogoMark from "./Logo";

const LINKS = [
  { label: "MEN", cat: "men" },
  { label: "WOMEN", cat: "women" },
  { label: "ACCESSORIES", cat: "accessories" },
];

export default function Navbar() {
  const { count, setCartOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const go = (cat) => {
    setMenuOpen(false);
    navigate(`/shop?cat=${cat}`);
  };

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0A0A0C]/70 border-b border-white/10">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <LogoMark className="h-8 w-8 transition-transform duration-300 group-hover:rotate-[-8deg]" />
            <span className="font-display font-extrabold tracking-[0.18em] text-sm md:text-base">
              DAFITEXCHANGE
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-10">
            {LINKS.map((l) => (
              <button
                key={l.cat}
                data-testid={`nav-link-${l.cat}`}
                onClick={() => go(l.cat)}
                className="text-xs font-medium tracking-[0.25em] text-white/60 hover:text-[#D3FF24] transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="cart-open-button"
              onClick={() => setCartOpen(true)}
              className="relative p-2.5 text-white/80 hover:text-[#D3FF24] transition-colors duration-300"
              aria-label="Open cart"
            >
              <ShoppingBag className="h-5 w-5" />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-0.5 -right-0.5 h-5 min-w-5 px-1 bg-[#D3FF24] text-[#0A0A0C] text-[10px] font-bold flex items-center justify-center"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              className="md:hidden p-2.5 text-white/80"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[#0A0A0C] flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-white/10">
              <LogoMark className="h-8 w-8" />
              <button data-testid="mobile-menu-close" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="p-2">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.cat}
                  data-testid={`mobile-nav-${l.cat}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                  onClick={() => go(l.cat)}
                  className="text-left font-display font-black text-5xl tracking-tight text-white/90 hover:text-[#D3FF24] transition-colors py-3"
                >
                  {l.label}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
